import React, { useEffect, useRef } from "react";
import type { UIMessage } from "@ai-sdk/react";
import type { SubAgentId } from "@/features/ads";
import { useAdsWorkspaceStore } from "@/features/ads";
import { ChatListView, type ChatListViewProps } from "./ChatListView";
import { ChatPanel } from "./ChatPanel";
import { RunningAgentsPanel } from "./cards/RunningAgentsPanel";
import type { CompanionInputProps } from "./agent-chat/CompanionInput";
import type { AvatarHeroProps } from "./ChatPanelHeader";
import type {
  CompanionSubAgentPanelRenderContext,
  SubAgentHeaderVariant,
} from "./SubAgentChatPanel";
import type { CompanionTokenUsageSnapshot } from "../lib/companionChatRuntime";

interface CompanionChatLayoutProps {
  persona: { humanName: string; humanRole: string; colorHex: string };
  activeMessages: UIMessage[];
  inputProps: CompanionInputProps;
  isLoading?: boolean;
  isCompacting?: boolean;
  tokenUsage?: CompanionTokenUsageSnapshot | null;
  isListening: boolean;
  isSecureMicContext: boolean;
  avatarMuted: boolean;
  avatarSpeaking: boolean;
  voiceVolume?: number;
  setVoiceVolume?: (v: number) => void;
  avatarHero?: AvatarHeroProps;
  toggleListen: () => void;
  setAvatarMuted: (m: boolean) => void;
  voiceTranscriptRef?: React.RefObject<string>;
  timelineRef?: React.RefObject<{
    segments: unknown[];
    totalDurationMs: number;
    speakStartedAt: number;
  }>;
  subAgentHeaderVariant?: SubAgentHeaderVariant;
  onSubAgentBackToList?: (ctx: CompanionSubAgentPanelRenderContext) => void;
  renderSubAgentHeaderActions?: (ctx: CompanionSubAgentPanelRenderContext) => React.ReactNode;
  renderSubAgentAfterHeader?: (ctx: CompanionSubAgentPanelRenderContext) => React.ReactNode;
  renderSubAgentContent?: (ctx: CompanionSubAgentPanelRenderContext) => React.ReactNode | undefined;
  renderSubAgentToolPart?: (
    agentId: SubAgentId,
    part: Record<string, unknown>,
    index: number,
  ) => React.ReactNode;
  listProps?: Omit<ChatListViewProps, "isLoading" | "onStop">;
}

export function CompanionChatLayout({
  persona,
  activeMessages,
  inputProps,
  isLoading,
  isCompacting,
  tokenUsage,
  isListening,
  isSecureMicContext,
  avatarMuted,
  avatarSpeaking,
  voiceVolume,
  setVoiceVolume,
  avatarHero,
  toggleListen,
  setAvatarMuted,
  voiceTranscriptRef,
  timelineRef,
  subAgentHeaderVariant,
  onSubAgentBackToList,
  renderSubAgentHeaderActions,
  renderSubAgentAfterHeader,
  renderSubAgentContent,
  renderSubAgentToolPart,
  listProps,
}: CompanionChatLayoutProps) {
  const viewLevel = useAdsWorkspaceStore((s) => s.companionViewLevel);
  const selectedChatAgent = useAdsWorkspaceStore((s) => s.selectedChatAgent);

  // On a fresh session (no sub-agents revealed yet) the chat list is essentially
  // just Marcus, so jump straight into Marcus's chat instead of making the user
  // tap through an empty list. Guarded by a ref so navigating back to the list
  // doesn't immediately bounce the user back into the chat.
  const hasAutoOpenedMarcusRef = useRef(false);
  useEffect(() => {
    if (hasAutoOpenedMarcusRef.current) return;
    hasAutoOpenedMarcusRef.current = true;
    const state = useAdsWorkspaceStore.getState();
    if (state.companionViewLevel === "list" && state.revealedSubAgents.length === 0) {
      state.selectChatAgent(null);
    }
  }, []);

  // Plain conditional render (no AnimatePresence): mode="wait" + absolute-positioned
  // motion.divs was leaving the exiting panel in the DOM on rapid Marcus → list →
  // sub-agent → Marcus navigation, stacking ghost headers/message-lists beside the
  // active view. Only one view mounts at a time now — keyed on the view so React
  // tears the previous tree down cleanly on switch.
  return (
    <div className="flex-1 relative overflow-hidden min-h-0">
      {/* Hidden polling host: keeps Banan + Vid job pollers mounted across
          Marcus ↔ sub-agent ↔ chat-list swaps, so completed dispatches persist
          assets + fire toasts even when the user isn't viewing Marcus. */}
      <div className="hidden">
        <RunningAgentsPanel />
      </div>
      {viewLevel === "list" ? (
        <div key="chat-list" className="absolute inset-0 w-full h-full">
          <ChatListView {...listProps} isLoading={isLoading} onStop={inputProps.onStop} />
        </div>
      ) : (
        <div
          key={`chat-panel-${selectedChatAgent ?? "marcus"}`}
          className="absolute inset-0 w-full h-full"
        >
          <ChatPanel
            persona={persona}
            activeMessages={activeMessages}
            inputProps={inputProps}
            isLoading={isLoading}
            isCompacting={isCompacting}
            tokenUsage={tokenUsage}
            isListening={isListening}
            isSecureMicContext={isSecureMicContext}
            avatarMuted={avatarMuted}
            avatarSpeaking={avatarSpeaking}
            voiceVolume={voiceVolume}
            setVoiceVolume={setVoiceVolume}
            avatarHero={avatarHero}
            toggleListen={toggleListen}
            setAvatarMuted={setAvatarMuted}
            voiceTranscriptRef={voiceTranscriptRef}
            timelineRef={timelineRef}
            subAgentHeaderVariant={subAgentHeaderVariant}
            onSubAgentBackToList={onSubAgentBackToList}
            renderSubAgentHeaderActions={renderSubAgentHeaderActions}
            renderSubAgentAfterHeader={renderSubAgentAfterHeader}
            renderSubAgentContent={renderSubAgentContent}
            renderSubAgentToolPart={renderSubAgentToolPart}
          />
        </div>
      )}
    </div>
  );
}
